import {
  ClaimsApi,
  Configuration,
  DocumentsApi,
  EmailApi,
  FetchError,
  ResponseError,
  SettingsApi,
  type ClaimPresentPositions,
  type ClaimResponse,
  type DocumentInfo,
  type UpdatePresentPositionRequest,
  type UpdatePresentPositionResponse,
  type Participant,
  type ParticipantsResponse,
  type AdhocContact,
  type AdhocContactsResponse,
  type AddAdhocContactRequest,
  type AddAdhocContactResponse,
  type CreateCaseNoteRequest,
  type CreateCaseNoteResponse,
  type WorkPackage,
  type WorkPackagesResponse,
  type LabourCode,
  type LabourCodesResponse,
  type BillingRole,
  type BillingRolesResponse,
  type CreateTimeEntryRequest,
  type CreateTimeEntryResponse,
  type TimeEntryStatus,
  type TimeEntryStatusesResponse,
  type RepliconUserStatusResponse,
  type UpdateTaskRequest,
  type UpdateTaskResponse,
  type ClaimTasksResponse,
  type ClaimTask as SdkClaimTask,
} from '@api-sdk'
import { ClaimSearchResult } from '../types/claim'
import type { Contact } from '../types/contact'
import type { ClaimTask } from '../types/task'

// Configuration constants
const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_BASE_URL || 'https://localhost:7001',
} as const

// Error messages
const ERROR_MESSAGES = {
  UNAUTHORIZED: 'Unauthorized: Please re-authenticate.',
  BAD_REQUEST: 'Bad request: Please provide a search query.',
  SEARCH_FAILED: (status: number) => `Search failed with status ${status}`,
  REQUEST_FAILED: (status: number) => `Request failed with status ${status}`,
} as const

// HTTP status codes
const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  BAD_REQUEST: 400,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
} as const

// Save failures the backend explains in the body, rather than leaving to a generic message.
const EXPLAINED_SAVE_FAILURES = new Set<number>([
  HTTP_STATUS.BAD_REQUEST,
  HTTP_STATUS.CONFLICT,
  HTTP_STATUS.INTERNAL_SERVER_ERROR,
])

// 60s: some MC1-backed calls (claim details, participants) can be slow, so allow
// headroom before aborting rather than surfacing a premature timeout error.
const TIMEOUT_MS = 60000

export class ApiUnauthorizedError extends Error {
  constructor(message: string = ERROR_MESSAGES.UNAUTHORIZED) {
    super(message)
    this.name = 'ApiUnauthorizedError'
  }
}

export class ApiTimeoutError extends Error {
  constructor(message: string = 'Request timed out. Please check your connection and try again.') {
    super(message)
    this.name = 'ApiTimeoutError'
  }
}

export class ApiNetworkError extends Error {
  constructor(message: string = 'Network error. Please check your connection and try again.') {
    super(message)
    this.name = 'ApiNetworkError'
  }
}

// The claim does not exist or the user cannot access it (HTTP 404). Retrying cannot help.
export class ApiNotFoundError extends Error {
  constructor(message: string = 'Not found.') {
    super(message)
    this.name = 'ApiNotFoundError'
  }
}

// A business rule enforced by an MC1 stored proc rejected the request (HTTP 400). Unlike a
// transport failure, retrying the same values can never succeed, so callers surface the
// proc's own reason rather than a generic "please try again".
export class ApiValidationError extends Error {
  readonly messages: string[]
  constructor(messages: string[]) {
    super(messages.join(' '))
    this.name = 'ApiValidationError'
    this.messages = messages
  }
}

// The procs report rule failures as { errors: [{ code, message }] } with messages like
// "[!] Msg 105 - Adhoc Contact / Adhoc Email combo already exists". The shape isn't in the
// OpenAPI spec, so it's parsed defensively — an unexpected body yields no messages and the
// caller falls back to generic handling.
// The backend sends a list for a 400 and a single object for the others (404, 409, 500), so both
// shapes are read. A bare RFC-9110 ProblemDetails has neither and yields nothing, which is the
// signal that the request never reached the endpoint.
interface ValidationErrorBody {
  errors?: { code?: string; message?: string }[]
  code?: string
  message?: string
}

// Drops the proc's "[!] Msg 105 - " prefix, which is diagnostic noise to the user.
const PROC_MESSAGE_PREFIX = /^\s*\[!]\s*Msg\s*\d+\s*-\s*/i

const clean = (message: string | undefined): string => (message ?? '').replace(PROC_MESSAGE_PREFIX, '').trim()

async function readValidationMessages(response: Response): Promise<string[]> {
  try {
    const body = (await response.json()) as ValidationErrorBody
    if (body.errors?.length) return body.errors.map((e) => clean(e.message)).filter(Boolean)
    return [clean(body.message)].filter(Boolean)
  } catch {
    // Not JSON, or the body was already consumed — fall back to generic handling.
    return []
  }
}

/**
 * Creates an AbortSignal that times out after TIMEOUT_MS.
 * Merges with an optional external signal.
 */
function createTimeoutSignal(externalSignal?: AbortSignal): AbortSignal {
  const signals: AbortSignal[] = [AbortSignal.timeout(TIMEOUT_MS)]
  if (externalSignal) signals.push(externalSignal)
  return AbortSignal.any(signals)
}

/**
 * Create API configuration with access token
 */
function createApiConfig(token: string): Configuration {
  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return new Configuration({
    basePath: API_CONFIG.BASE_URL,
    headers,
  })
}

function handleResponseError(error: ResponseError): never {
  const status = error.response.status
  if (status === HTTP_STATUS.UNAUTHORIZED) {
    throw new ApiUnauthorizedError()
  }
  if (status === HTTP_STATUS.BAD_REQUEST) {
    throw new Error(ERROR_MESSAGES.BAD_REQUEST)
  }
  throw new Error(ERROR_MESSAGES.SEARCH_FAILED(status))
}

function handleError(error: unknown): never {
  if (error instanceof ResponseError) {
    return handleResponseError(error)
  }
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    throw new ApiTimeoutError()
  }
  if (error instanceof FetchError) {
    throw new ApiNetworkError()
  }
  throw error
}

/**
 * Search claims by query string
 */
export async function searchClaims(
  query: string,
  searchType: string,
  token: string,
  signal?: AbortSignal
): Promise<ClaimSearchResult[]> {
  const config = createApiConfig(token)
  const api = new ClaimsApi(config)

  try {
    const fullQuery = searchType ? `${searchType}:${query}` : query
    const timeoutSignal = createTimeoutSignal(signal)
    const response = await api.searchClaimsRaw({ query: fullQuery }, { signal: timeoutSignal })
    const data = (await response.raw.json()) as ClaimSearchResult[]
    return data.map((claim) => ({
      ...claim,
      claimTitle: claim.claimTitle || claim.lossLocation || claim.insured || claim.claimNumber,
    }))
  } catch (error) {
    handleError(error)
  }
}

interface RecentClaimResponse {
  claimNumber: string
  primaryClient: string | null
  insured: string | null
  broker: string | null
  dateOfLoss: string
  lastAccessedAt: string
  lossLocation: string | null
}

function mapRecentClaim(claim: RecentClaimResponse): ClaimSearchResult {
  return {
    claimNumber: claim.claimNumber,
    claimTitle: claim.lossLocation || claim.insured || claim.claimNumber,
    insured: claim.insured || '',
    primaryClient: claim.primaryClient || '',
    policyType: '',
    presentPosition: '',
    dateOfLoss: claim.dateOfLoss,
    broker: claim.broker || '',
    totalReserves: 0,
    currency: '',
    lossLocation: null,
  }
}

/**
 * Get recent claims
 */
export async function recentClaims(token: string): Promise<ClaimSearchResult[]> {
  const config = createApiConfig(token)
  const api = new ClaimsApi(config)

  try {
    const timeoutSignal = createTimeoutSignal()
    const response = await api.getRecentClaimsRaw({ signal: timeoutSignal })
    const data = (await response.raw.json()) as RecentClaimResponse[]
    return data.map(mapRecentClaim)
  } catch (error) {
    handleError(error)
  }
}

function createApi(token: string) {
  return new ClaimsApi(createApiConfig(token))
}

function createDocumentsApi(token: string) {
  return new DocumentsApi(createApiConfig(token))
}

function createSettingsApi(token: string) {
  return new SettingsApi(createApiConfig(token))
}

async function apiCall<T>(fn: () => Promise<{ raw: Response }>): Promise<T> {
  try {
    const response = await fn()
    return (await response.raw.json()) as T
  } catch (error) {
    return handleError(error)
  }
}

export async function getClaim(claimNumber: string, token: string): Promise<ClaimResponse> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  return apiCall<ClaimResponse>(() => api.getClaimRaw({ claimNumber }, { signal }))
}

export async function tryGetClaim(claimNumber: string, token: string): Promise<ClaimResponse | null> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.getClaimRaw({ claimNumber }, { signal })
    return (await response.raw.json()) as ClaimResponse
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === 404) {
      return null
    }
    return handleError(error)
  }
}

export async function getClaimFolderId(claimNumber: string, token: string): Promise<string | null> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.getClaimFolderIdRaw({ claimNumber }, { signal })
    return await response.value()
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === 404) {
      return null
    }
    return handleError(error)
  }
}

export async function getClaimDocuments(folderId: string, token: string): Promise<DocumentInfo[]> {
  const api = createDocumentsApi(token)
  const signal = createTimeoutSignal()
  return apiCall<DocumentInfo[]>(() => api.getDocumentsRaw({ folderId }, { signal }))
}

export async function uploadDocumentToFolder(
  folderId: string,
  file: File,
  token: string,
  cancelSignal?: AbortSignal
): Promise<DocumentInfo> {
  const api = createDocumentsApi(token)
  const signal = createTimeoutSignal(cancelSignal)
  return apiCall<DocumentInfo>(() => api.uploadDocumentRaw({ folderId, file }, { signal }))
}

export async function getPresentPositionOptions(claimNumber: string, token: string): Promise<ClaimPresentPositions[]> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  return apiCall<ClaimPresentPositions[]>(() => api.getClaimPresentPositionsRaw({ claimNumber }, { signal }))
}

// ─── Time Entry lookups (MSD-355) ─────────────────────────────────────────────
//
// All three come from Replicon: work packages are the tasks at the top of the claim's
// project, labour codes hang off one work package, and billing roles are whatever the
// signed-in user may book under on the timesheet covering a given date.

// A 404 means Replicon has no project for the claim, so it has no work packages either. That is
// an answer rather than a failure — retrying can't change it — so it reads as an empty list.
export async function getWorkPackages(claimNumber: string, token: string): Promise<WorkPackage[]> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.getClaimWorkPackagesRaw({ claimNumber }, { signal })
    const body = (await response.raw.json()) as WorkPackagesResponse
    return body.workPackages ?? []
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === 404) {
      return []
    }
    return handleError(error)
  }
}

export async function getLabourCodes(claimNumber: string, workPackageId: string, token: string): Promise<LabourCode[]> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  const response = await apiCall<LabourCodesResponse>(() =>
    api.getClaimLabourCodesRaw({ claimNumber, workPackageId }, { signal })
  )
  return response.labourCodes ?? []
}

// `date` picks the timesheet the roles are read from, and is **required**: the backend used to
// fall back to today in UTC, which is the wrong day for anyone far enough ahead of it, and
// reading roles creates a timesheet — so a wrong guess left one behind on a day nobody asked
// about. Only the add-in knows the user's local date. The SDK sends the UTC half of an ISO
// string, so the caller passes a UTC-midnight Date to keep the date the user picked
// (see toUtcDateOnly in useTimeEntryOptions).
export async function getBillingRoles(claimNumber: string, token: string, date: Date): Promise<BillingRole[]> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  const response = await apiCall<BillingRolesResponse>(() =>
    api.getClaimBillingRolesRaw({ claimNumber, date }, { signal })
  )
  return response.billingRoles ?? []
}

// Billing statuses are the same list for every claim and user, so this one takes no
// claim number (MSD-355).
export async function getTimeEntryStatuses(token: string): Promise<TimeEntryStatus[]> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  const response = await apiCall<TimeEntryStatusesResponse>(() => api.getTimeEntryStatusesRaw({ signal }))
  return response.statuses ?? []
}

// Whether the signed-in user has a Replicon account, which decides whether the Time Entry tab
// holds the booking form or the Vision link. When Replicon is unreachable the backend answers 500
// rather than false, so this throws and the caller shows Time Entry as unavailable.
// No parameters, so `{ signal }` is the only argument (initOverrides), as with getFeatureFlags.
export async function getRepliconUserStatus(token: string): Promise<boolean> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  const response = await apiCall<RepliconUserStatusResponse | null>(() => api.getRepliconUserStatusRaw({ signal }))
  return response?.isRepliconUser === true
}

// The generated model now carries warnings, statusId and statusName, so this is the response
// as-is. Kept as a named alias because callers refer to it and because `apiCall` is bypassed
// below — the `date` field is an ISO string at runtime despite being typed Date.
export type CreateTimeEntryResult = CreateTimeEntryResponse

// Books time against the claim in Replicon (MSD-355). The service rejects bad input with
// 400 { errors: [{ code, message }] } — the same shape the MC1 procs use — so its reason
// is unwrapped into an ApiValidationError rather than a generic bad-request message.
export async function createTimeEntry(
  claimNumber: string,
  request: CreateTimeEntryRequest,
  token: string
): Promise<CreateTimeEntryResult> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.createClaimTimeEntryRaw({ claimNumber, createTimeEntryRequest: request }, { signal })
    return (await response.raw.json()) as CreateTimeEntryResult
  } catch (error) {
    // A rejected save is worth reading whatever its status: a 409 says the day's timesheet is
    // closed and retrying can never work, and a 500 names which server-side failure it was.
    // Showing "Please try again" over either of those sends the user round in circles.
    if (error instanceof ResponseError && EXPLAINED_SAVE_FAILURES.has(error.response.status)) {
      const messages = await readValidationMessages(error.response)
      if (messages.length > 0) throw new ApiValidationError(messages)
    }
    return handleError(error)
  }
}

export async function updatePresentPosition(
  claimNumber: string,
  request: UpdatePresentPositionRequest,
  token: string
): Promise<UpdatePresentPositionResponse> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  return apiCall<UpdatePresentPositionResponse>(() =>
    api.updateClaimPresentPositionRaw({ claimNumber, updatePresentPositionRequest: request }, { signal })
  )
}

// ─── Participants & ad-hoc contacts (Phase 2, Epic 2.6 / 2.7) ──────────────────
// Both come from real read endpoints:
//   GET /api/claims/{claimNumber}/participants
//   GET /api/claims/{claimNumber}/adhoc-contacts
// Neither SDK type carries an id, so a stable React key is derived from email,
// falling back to the list position. Unmatched email recipients are surfaced as
// ad-hoc contacts client-side in ContactsTab (display only — not persisted).

// Ids are prefixed per list so a person appearing in both lists can't produce
// colliding React keys. Prefer mobile, then phone, for the displayed number (MSD-657)
// — `||` because an empty mobile must fall through to phone.
//
// MC1 pads its string fields with whitespace, so every field is trimmed here at the
// boundary instead of at each use. Padding is invisible on screen but compares
// unequal, and it has cost us twice: a padded mobile masked a real phone number
// (MSD-657), and a padded email stopped matching the same address coming from
// Outlook, so a participant was listed twice and never promoted to the top of the
// Contacts tab (MSD-642). Normalising once keeps every comparison downstream honest.
function mapParticipantToContact(p: Participant, index: number): Contact {
  const email = p.email?.trim() ?? ''
  const mobile = p.mobile?.trim()
  const phone = p.phone?.trim()
  const role = p.role?.trim() ?? ''
  return {
    // Keyed by email *and* role: MC1 sends one row per party-role pair, so someone holding
    // several roles on a claim arrived as several contacts sharing one id — React then had
    // duplicate keys in the Contacts list (MSD-642). Role keeps them distinct while the id
    // stays stable across refetches, which index alone would not.
    id: role ? `participant-${email || index}-${role}` : `participant-${email || index}`,
    name: p.fullName?.trim() ?? '',
    role,
    email,
    phone: mobile || phone || undefined,
  }
}

function mapAdhocToContact(a: AdhocContact, index: number): Contact {
  const email = a.email?.trim() ?? ''
  return {
    id: `adhoc-${email || index}`,
    name: a.name?.trim() ?? '',
    role: a.role?.trim() ?? '',
    email,
    isAdHoc: true,
  }
}

export async function getClaimParticipants(claimNumber: string, token: string): Promise<Contact[]> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  const response = await apiCall<ParticipantsResponse>(() => api.getClaimParticipantsRaw({ claimNumber }, { signal }))
  // Backend serializes an empty list as null — treat as no participants.
  return (response.participants ?? []).map((p, i) => mapParticipantToContact(p, i))
}

export async function getClaimAdhocContacts(claimNumber: string, token: string): Promise<Contact[]> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  const response = await apiCall<AdhocContactsResponse>(() => api.getClaimAdhocContactsRaw({ claimNumber }, { signal }))
  return (response.adhocContacts ?? []).map((a, i) => mapAdhocToContact(a, i))
}

// Persists an unmatched email recipient as an MC1 ad-hoc contact (MSD-256).
// The backend proc requires both name and email; the form enforces that. It also rejects a
// name+email pair already on the claim (Msg 105) — that comes back as a 400, so the proc's
// reason is unwrapped into an ApiValidationError instead of a generic bad-request error.
export async function addAdhocContact(
  claimNumber: string,
  request: AddAdhocContactRequest,
  token: string
): Promise<AddAdhocContactResponse> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.addClaimAdhocContactRaw({ claimNumber, addAdhocContactRequest: request }, { signal })
    return (await response.raw.json()) as AddAdhocContactResponse
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === HTTP_STATUS.BAD_REQUEST) {
      const messages = await readValidationMessages(error.response)
      if (messages.length > 0) throw new ApiValidationError(messages)
    }
    return handleError(error)
  }
}

// Creates a File Note case note against a claim (MSD-236/237).
export async function createFileNoteCaseNote(
  claimNumber: string,
  request: CreateCaseNoteRequest,
  token: string
): Promise<CreateCaseNoteResponse> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  return apiCall<CreateCaseNoteResponse>(() =>
    api.createFileNoteCaseNoteRaw({ claimNumber, createCaseNoteRequest: request }, { signal })
  )
}

// The app's ClaimTask uses ISO date strings and a string id, so the SDK shape is
// normalised here rather than in the components.
//
// Dates accept string as well as Date: apiCall reads response.raw.json() directly,
// so the SDK's FromJSON converters never run and these arrive as ISO strings at
// runtime even though the generated types say Date.
//
// MC1 reports "no date" as a zero/default date rather than null, so anything at or
// before 1900 is treated as absent — otherwise every task would show a hold date.
const NO_DATE_BEFORE_YEAR = 1900

// MC1 sends timezone-less timestamps ("2099-04-15T00:00:00"). Those are calendar
// dates, not instants: `new Date` reads them as local midnight, and converting that
// to UTC shifts the day backwards anywhere east of Greenwich. So the date part is
// taken verbatim from the string and only real Date objects go via UTC getters.
const DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/

function toIsoDate(value: Date | string | null | undefined): string | null {
  if (value == null) return null

  if (typeof value === 'string') {
    const parts = DATE_PREFIX.exec(value)
    if (!parts) return null
    if (Number(parts[1]) <= NO_DATE_BEFORE_YEAR) return null
    return `${parts[1]}-${parts[2]}-${parts[3]}`
  }

  if (Number.isNaN(value.getTime())) return null
  if (value.getUTCFullYear() <= NO_DATE_BEFORE_YEAR) return null
  return value.toISOString().slice(0, 10)
}

// Trimmed here for the same reason the contact mappers above are: MC1 pads its string
// fields, the padding is invisible on screen, and it compares unequal. On a task it
// reaches further than it looks — `getTaskUrgency` matches the status against a fixed
// list, the card builds its aria-labels out of the type, and the event reminder puts
// the type straight into an Outlook subject. Normalising once here means none of them
// has to trim again.
//
// The API's `holdDescription` is misnamed: outlookaddin_getClaimTasks fills it from
// `ta.description`, the task's own description. The hold reason goes to the task's
// on-hold history and no proc reads it back, so it is mapped to `taskDescription`.
// It keeps its null-versus-undefined distinction: null is MC1 saying the task has no
// description, undefined is the field not being in the payload at all.
// `onHoldStatus` is not in the generated model yet — it is declared here so the mapping
// can read it, and this intersection should go when the SDK is regenerated.
function toClaimTask(task: SdkClaimTask & { onHoldStatus?: string | null }): ClaimTask {
  return {
    id: String(task.taskId ?? '').trim(),
    type: task.taskType?.trim() ?? '',
    status: task.status?.trim() ?? '',
    targetDate: toIsoDate(task.targetDate) ?? '',
    assignedTo: task.assigned?.trim() ?? '',
    // MC1 sends the hold as a padded Y/N flag rather than a status, so anything that is
    // not a clear "Y" — including the field being absent — reads as not held.
    isOnHold: (task.onHoldStatus ?? '').trim().toUpperCase() === 'Y',
    holdDate: toIsoDate(task.onHoldUntil),
    taskDescription: typeof task.holdDescription === 'string' ? task.holdDescription.trim() : task.holdDescription,
  }
}

// Open tasks for a claim (MSD-682): GET /api/claims/{claimNumber}/tasks
export async function getClaimTasks(claimNumber: string, token: string): Promise<ClaimTask[]> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  const response = await apiCall<ClaimTasksResponse>(() => api.getClaimTasksRaw({ claimNumber }, { signal }))
  return (response.tasks ?? []).map(toClaimTask)
}

// Puts a task on hold: the On Hold Until date and the description in one call.
// PATCH /api/claims/{claimNumber}/tasks/{taskId}
// The on-hold proc refuses a hold with 400 { errors: [{ code, message }] } — task not open,
// already on hold, date not after today — so its reason is unwrapped into an
// ApiValidationError rather than a generic bad-request message.
export async function updateClaimTask(
  claimNumber: string,
  taskId: number,
  request: UpdateTaskRequest,
  token: string
): Promise<UpdateTaskResponse> {
  const api = createApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.updateClaimTaskRaw({ claimNumber, taskId, updateTaskRequest: request }, { signal })
    return (await response.raw.json()) as UpdateTaskResponse
  } catch (error) {
    if (error instanceof ResponseError && error.response.status === HTTP_STATUS.BAD_REQUEST) {
      const messages = await readValidationMessages(error.response)
      if (messages.length > 0) throw new ApiValidationError(messages)
    }
    return handleError(error)
  }
}

// Backend-controlled feature flags (e.g. EnableContactsTab) used to show or hide UI.
export async function getFeatureFlags(token: string): Promise<Record<string, boolean>> {
  const api = createSettingsApi(token)
  const signal = createTimeoutSignal()
  // The endpoint takes no parameters, so the single argument is initOverrides — unlike
  // the calls above, which pass (params, { signal }). Adding a `{}` first argument here
  // would silently drop the timeout signal without a type error.
  return apiCall<Record<string, boolean>>(() => api.getFeatureFlagsRaw({ signal }))
}

export async function downloadClaimDocument(id: string, token: string): Promise<Blob> {
  const api = createDocumentsApi(token)
  const signal = createTimeoutSignal()
  try {
    const response = await api.downloadDocumentRaw({ id }, { signal })
    return await response.raw.blob()
  } catch (error) {
    return handleError(error)
  }
}

// --- Simple Attach (Attach to MC1) -------------------------------------------------------------

export interface SimpleAttachResponse {
  claimReference: string
}

// The EML is uploaded as a file rather than posted as base64 in JSON: a message with attachments is
// easily larger than the server's default request body limit once base64 has inflated it by a third.
export async function postSimpleAttach(claimNumber: string, eml: Blob, token: string): Promise<SimpleAttachResponse> {
  const api = new EmailApi(createApiConfig(token))
  const signal = createTimeoutSignal()
  const file = new File([eml], 'original.eml', { type: 'message/rfc822' })
  try {
    const response = await api.simpleAttachEmailRaw({ claimNumber, file }, { signal })
    return (await response.raw.json()) as SimpleAttachResponse
  } catch (error) {
    // The API answers 404 when the claim does not exist or the user cannot access it.
    if (error instanceof ResponseError && error.response.status === 404) {
      throw new ApiNotFoundError()
    }
    return handleError(error)
  }
}
